import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Wand2, Copy, RefreshCw, MessageSquare } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn, copyFormattedContent } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface ContentReviewEditorProps {
  content: string;
  onChange: (content: string) => void;
  platform?: string;
  metadata?: Record<string, any>;
  improvedContent?: string;
  onImprovedContentChange?: (content: string) => void;
}

export const ContentReviewEditor = ({ 
  content, 
  onChange, 
  platform = 'blog',
  metadata,
  improvedContent: externalImprovedContent,
  onImprovedContentChange
}: ContentReviewEditorProps) => {
  const [isReviewing, setIsReviewing] = useState(false);
  const [localImprovedContent, setLocalImprovedContent] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string>("");
  const [showFeedback, setShowFeedback] = useState<boolean>(false);
  const [versionToRegenerate, setVersionToRegenerate] = useState<'version1' | 'version2'>('version2');
  const { toast } = useToast();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Use external improvedContent if provided, otherwise use local state
  const improvedContent = externalImprovedContent || localImprovedContent;
  
  // Handle changes to improved content
  const handleImprovedContentChange = (value: string) => {
    if (onImprovedContentChange) {
      onImprovedContentChange(value);
    } else {
      setLocalImprovedContent(value);
    }
  };

  const handleReviewContent = async () => {
    // Validate we have content to review
    if (!content || content.trim() === '') {
      toast({
        title: "No content to review",
        description: "Please enter some content before requesting a review.",
        variant: "destructive",
      });
      return;
    }
    
    setIsReviewing(true);
    setErrorMessage(null); // Clear any previous errors
    
    try {
      // Create a simplified request body without unnecessary nested objects
      const requestBody = { 
        content: versionToRegenerate === 'version1' ? content : (improvedContent || content),
        platform: platform || 'generic', // Ensure platform is never undefined
        feedback: feedback.trim() || undefined,
        // Include only essential metadata to avoid issues
        metadata: {
          ...((metadata && typeof metadata === 'object') ? metadata : {}),
          feedback: feedback.trim() || undefined,
          targetVersion: versionToRegenerate, // Add explicit target version
          hasImprovedContent: !!improvedContent, // Flag if improvedContent exists
          contentId: metadata?.id || null // Add content ID for proper updating
        }
      };
      
      console.log('Sending review request:', JSON.stringify({
        ...requestBody,
        content: requestBody.content.substring(0, 100) + '...' // Log truncated content for debugging
      }));
      
      const { data, error } = await supabase.functions.invoke('review-generated-content', {
        body: requestBody
      });

      if (error) {
        console.error('Supabase function error:', error);
        throw new Error('Unable to process content at this time');
      }
      
      if (!data) {
        console.error('No data returned from review-content function');
        throw new Error('Unable to process content at this time');
      }
      
      // Handle both possible response formats (improvedContent or analyzedContent)
      const updatedContent = data.improvedContent || data.analyzedContent;
      
      if (!updatedContent) {
        console.error('No improved content in response:', data);
        throw new Error('Unable to generate improved content');
      }

      // Update the appropriate content based on which version is being regenerated
      if (versionToRegenerate === 'version1') {
        onChange(updatedContent);
      } else {
        // Update the improved content
        if (onImprovedContentChange) {
          onImprovedContentChange(updatedContent);
        } else {
          setLocalImprovedContent(updatedContent);
        }
      }

      // Reset feedback after successful regeneration
      setFeedback("");
      setShowFeedback(false);

      toast({
        title: `${versionToRegenerate === 'version1' ? 'Version 1' : 'Version 2'} Regenerated`,
        description: feedback 
          ? "Your feedback has been incorporated into the new version." 
          : "Content has been regenerated successfully!",
      });
    } catch (error) {
      console.error('Review error:', error);
      // Set a user-friendly error message regardless of the actual error
      setErrorMessage("We couldn't generate new content right now. Please try again later.");
        
      toast({
        title: "Regeneration Failed",
        description: "Could not regenerate content at this time. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsReviewing(false);
    }
  };

  const handleCopy = (text: string) => {
    if (!text) {
      toast({
        title: "Cannot copy",
        description: "There is no content to copy.",
        variant: "destructive"
      });
      return;
    }
    
    copyFormattedContent(text, {
      onSuccess: () => {
        setCopied(true);
        toast({
          title: "Copied to clipboard",
          description: "Content has been copied to clipboard",
        });
        setTimeout(() => setCopied(false), 2000);
      },
      onError: (error) => {
        console.error("Copy failed:", error);
        toast({
          title: "Error",
          description: "Failed to copy content to clipboard",
          variant: "destructive",
        });
      }
    });
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-end mb-2">
        {improvedContent ? (
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  disabled={isReviewing || !content}
                  size="sm"
                  className="flex items-center gap-1"
                >
                  {isReviewing ? (
                    <>
                      <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                      Regenerating...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="mr-1 h-4 w-4" />
                      Improve & Regenerate
                      <ChevronDown className="h-3 w-3 opacity-70" />
                    </>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem 
                  onClick={() => {
                    setVersionToRegenerate('version1');
                    setShowFeedback(true);
                    setErrorMessage(null);
                  }}
                  className="cursor-pointer"
                >
                  Improve Version 1
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => {
                    setVersionToRegenerate('version2');
                    setShowFeedback(true);
                    setErrorMessage(null);
                  }}
                  className="cursor-pointer"
                >
                  Improve Version 2
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowFeedback(true);
                setErrorMessage(null);
                // Default to version2 when using Review & Improve
                setVersionToRegenerate('version2');
              }}
              disabled={isReviewing || !content}
              size="sm"
            >
              {isReviewing ? (
                <>
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  Reviewing...
                </>
              ) : (
                <>
                  <Wand2 className="mr-1 h-4 w-4" />
                  Review & Improve
                </>
              )}
            </Button>
          </div>
        )}
      </div>
      
      {/* Display error message even when feedback form isn't showing */}
      {errorMessage && !showFeedback && (
        <div className="mb-3 p-3 text-sm bg-amber-50 border border-amber-200 rounded-md text-amber-800">
          <div className="flex items-center mb-1">
            <RefreshCw className="h-4 w-4 mr-2" />
            <p className="font-medium">Content Generation Paused</p>
          </div>
          <p className="ml-6">We're experiencing temporary issues with our AI service. Please try again later.</p>
        </div>
      )}

      {showFeedback && (
        <div className="mb-3">
          <div className="mb-2 flex items-center gap-2">
            <div className="text-sm font-medium">Improving: {versionToRegenerate === 'version1' ? 'Version 1' : 'Version 2'}</div>
            <Badge variant="outline" className={cn(
              "text-xs py-0 px-2",
              versionToRegenerate === 'version1' 
                ? "bg-blue-500/10 text-blue-500 border-blue-500/20" 
                : "bg-blue-500/10 text-blue-500 border-blue-500/20"
            )}>
              {versionToRegenerate === 'version1' ? 'Original' : 'Enhanced'}
            </Badge>
          </div>
          <Textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            className="w-full text-sm border-blue-500/20 focus-visible:ring-blue-500/20 bg-blue-50/50 dark:bg-blue-950/10"
            placeholder={`Instructions for improving ${versionToRegenerate === 'version1' ? 'Version 1' : 'Version 2'} (e.g., 'Make it more conversational', 'Add more data points', 'Focus more on benefits')`}
            rows={2}
          />
          <div className="flex justify-between items-center">
            <p className="text-xs text-muted-foreground mt-1 ml-1">
              Your feedback will be incorporated when regenerating this version.
            </p>
            <Button 
              variant="default"
              size="sm"
              onClick={handleReviewContent}
              disabled={isReviewing}
              className="mt-2 bg-blue-500 hover:bg-blue-600 text-white"
            >
              {isReviewing ? (
                <>
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-1 h-4 w-4" />
                  Apply Feedback & Regenerate
                </>
              )}
            </Button>
          </div>
          
          {errorMessage && (
            <div className="mt-2 p-3 text-sm bg-amber-50 border border-amber-200 rounded-md text-amber-800">
              <div className="flex items-center mb-1">
                <RefreshCw className="h-4 w-4 mr-2" />
                <p className="font-medium">Content Generation Paused</p>
              </div>
              <p className="ml-6">We're experiencing temporary issues with our AI service. Please try again later.</p>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 flex-1 overflow-hidden min-h-0">
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-sm font-medium">Version 1</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleCopy(content)}
              className="h-6"
            >
              <Copy className="h-3 w-3 mr-1" />
              Copy
            </Button>
          </div>
          <Textarea
            value={content}
            onChange={(e) => onChange(e.target.value)}
            className={cn(
              "flex-1 resize-none p-2 font-mono text-sm",
              "border-blue-500/20 focus-visible:ring-blue-500/20",
              "bg-card/50 backdrop-blur-sm",
              versionToRegenerate === 'version1' && showFeedback ? "ring-2 ring-blue-500/20" : ""
            )}
            placeholder={`Enter your ${platform || 'content'} here...`}
          />
        </div>

        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-sm font-medium">Version 2</h3>
            {improvedContent && (
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleCopy(improvedContent)}
                  className="h-6"
                >
                  <Copy className="h-3 w-3 mr-1" />
                  Copy
                </Button>
              </div>
            )}
          </div>
          
          {improvedContent ? (
            <Textarea
              value={improvedContent}
              onChange={(e) => handleImprovedContentChange(e.target.value)}
              className={cn(
                "flex-1 resize-none p-2 font-mono text-sm",
                "border-cyan-500/20 focus-visible:ring-cyan-500/20",
                "bg-cyan-500/5 backdrop-blur-sm",
                versionToRegenerate === 'version2' && showFeedback ? "ring-2 ring-blue-500/20" : ""
              )}
              placeholder="Reviewed content will appear here..."
            />
          ) : (
            <div className="flex flex-col items-center justify-center p-4 border border-dashed rounded-md bg-muted/50 flex-1">
              <Wand2 className="h-8 w-8 text-muted-foreground mb-2" />
              <p className="text-muted-foreground text-center text-sm">
                "Click \"Review & Improve\" to generate Version 2 of your content"
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 